import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { api } from '@/api/client';
import { AnnouncementPicture } from '@/components/AnnouncementPicture';
import { ErrorNote, OkNote, shortDate } from '@/components/ui';
import { useAuth } from '@/stores/auth';
import { confirmAction } from '@/stores/confirm';
import { useScreenIntent } from '@/stores/shellNav';
import { AnnouncementComposer } from './announcements/AnnouncementComposer';

type ListItem = Awaited<ReturnType<typeof fetchList>>['items'][number];

function fetchList() {
  // Every announcement the caller manages, at once: there are rarely more
  // than a few dozen, and the tab counts need them all anyway.
  return api.get('/api/v1/announcements', { take: 200 });
}

const TABS = [
  { id: 'ALL', label: 'All' },
  { id: 'LIVE', label: 'Live' },
  { id: 'SCHEDULED', label: 'Scheduled' },
  { id: 'DRAFT', label: 'Drafts' },
  { id: 'EXPIRED', label: 'Ended' },
  { id: 'ARCHIVED', label: 'Archived' },
] as const;
type TabId = (typeof TABS)[number]['id'];

const STATE_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  SCHEDULED: 'Scheduled',
  LIVE: 'Live',
  EXPIRED: 'Ended',
  ARCHIVED: 'Archived',
};

const EMPTY: Record<TabId, string> = {
  ALL: 'No announcements yet.',
  LIVE: 'Nothing is live right now.',
  SCHEDULED: 'Nothing is scheduled.',
  DRAFT: 'No drafts.',
  EXPIRED: 'Nothing has ended yet.',
  ARCHIVED: 'Nothing archived.',
};

/**
 * The announcements screen: what is live, scheduled and drafted, and the
 * composer for writing a new one. The list only shows what the caller can
 * manage; every button is a courtesy gate and the server checks again.
 */
export function Announcements() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<TabId>('ALL');
  const intent = useScreenIntent('home-announce');
  // "New announcement" on the Dashboard arrives here with the composer open.
  const [composing, setComposing] = useState<{ id?: string } | null>(() =>
    intent?.action === 'create' && can('com.announcement.create') ? {} : null,
  );
  const [note, setNote] = useState<string | null>(null);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['announcements'] });

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['announcements', 'list'],
    queryFn: fetchList,
  });

  const publish = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/announcements/${id}/publish`),
    onSuccess: () => {
      invalidate();
      setNote('Published. It reaches dashboards within a minute.');
    },
  });
  const unpublish = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/announcements/${id}/unpublish`),
    onSuccess: () => {
      invalidate();
      setNote('Taken down. It is a draft again, and no dashboard shows it.');
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete<{ outcome: 'deleted' | 'archived' }>(`/api/v1/announcements/${id}`),
    onSuccess: (r) => {
      invalidate();
      setNote(r.outcome === 'deleted' ? 'Draft deleted.' : 'Archived. It stays in the Archived tab for the record.');
    },
  });

  const busyId = [publish, unpublish, remove].find((m) => m.isPending)?.variables;
  const actionError = [publish, unpublish, remove].find((m) => m.isError)?.error;

  const closeComposer = useCallback(() => setComposing(null), []);
  const saved = useCallback(
    (message: string) => {
      setComposing(null);
      setNote(message);
      void queryClient.invalidateQueries({ queryKey: ['announcements'] });
    },
    [queryClient],
  );

  const canCreate = can('com.announcement.create');
  const canEdit = (a: ListItem) =>
    a.canManage && can('com.announcement.update') && (a.state === 'DRAFT' || can('com.announcement.post'));

  async function onRemove(a: ListItem) {
    const draft = a.state === 'DRAFT';
    const ok = await confirmAction(
      draft
        ? {
            title: 'Delete this draft?',
            message: `“${a.headlineEn}” will be gone for good.`,
            confirmLabel: 'Delete draft',
            danger: true,
          }
        : {
            title: 'Archive this announcement?',
            message: `“${a.headlineEn}” comes off every dashboard and moves to Archived, where it stays for the record.`,
            confirmLabel: 'Archive',
          },
    );
    if (ok) remove.mutate(a.id);
  }

  const items = data?.items ?? [];
  const count = (id: TabId) => (id === 'ALL' ? items.length : items.filter((a) => a.state === id).length);
  const shown = tab === 'ALL' ? items : items.filter((a) => a.state === tab);

  return (
    <div className="stack announcements">
      <div className="announcements__head">
        <div className="viewtoggle" role="tablist" aria-label="Show">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={`viewtoggle__btn${tab === t.id ? ' viewtoggle__btn--on' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
              {data && (
                <>
                  {' '}
                  <span className="announcements__count">{count(t.id)}</span>
                </>
              )}
            </button>
          ))}
        </div>
        {canCreate && (
          <button className="btn btn--primary" onClick={() => setComposing({})}>
            New announcement
          </button>
        )}
      </div>

      {note && <OkNote message={note} />}
      {actionError && <ErrorNote message={actionError instanceof Error ? actionError.message : 'That did not work.'} />}
      {isError && <ErrorNote message={error.message} />}
      {isPending && <p className="muted">Loading…</p>}

      {data && shown.length === 0 && (
        <div className="announcements__empty">
          <p className="announcements__emptytitle">{EMPTY[tab]}</p>
          {canCreate && tab !== 'ARCHIVED' && tab !== 'EXPIRED' && (
            <>
              <p className="muted">
                Write a headline, add a picture if you like, and publish. It shows on everyone’s Dashboard within a
                minute.
              </p>
              <button className="btn btn--primary" onClick={() => setComposing({})}>
                New announcement
              </button>
            </>
          )}
        </div>
      )}

      {shown.length > 0 && (
        <ul className="announcements__list">
          {shown.map((a) => {
            const editable = canEdit(a);
            return (
              <li key={a.id} className={`announcements__row announcements__row--${a.state.toLowerCase()}`}>
                <button
                  type="button"
                  className="announcements__open"
                  onClick={() => editable && setComposing({ id: a.id })}
                  disabled={!editable}
                  aria-label={editable ? `Edit: ${a.headlineEn}` : a.headlineEn}
                >
                  <span className="announcements__thumb">
                    {a.imageKey ? (
                      <AnnouncementPicture id={a.id} imageKey={a.imageKey} alt="" className="announcements__thumbimg" />
                    ) : (
                      <span className={`announcements__dot announcements__dot--${a.priority.toLowerCase()}`} aria-hidden="true" />
                    )}
                  </span>
                  <span className="announcements__main">
                    <span className="announcements__headline">
                      {a.priority === 'URGENT' && <span className="pill pill--alert">⚠ Urgent</span>}
                      {a.priority === 'IMPORTANT' && <span className="pill pill--warn">Important</span>}{' '}
                      {a.headlineEn}
                    </span>
                    <span className="announcements__meta">
                      {audienceLine(a)} · {windowLine(a)}
                    </span>
                  </span>
                  <StatePill state={a.state} />
                </button>
                {a.canManage && (
                  <span className="announcements__actions">
                    {can('com.announcement.post') && a.state === 'DRAFT' && (
                      <button className="btn btn--sm" disabled={busyId === a.id} onClick={() => publish.mutate(a.id)}>
                        Publish
                      </button>
                    )}
                    {can('com.announcement.post') && (a.state === 'LIVE' || a.state === 'SCHEDULED') && (
                      <button className="btn btn--sm" disabled={busyId === a.id} onClick={() => unpublish.mutate(a.id)}>
                        Take down
                      </button>
                    )}
                    {can('com.announcement.delete') && a.state !== 'ARCHIVED' && (
                      <button
                        className="btn btn--sm btn--ghost"
                        disabled={busyId === a.id}
                        onClick={() => void onRemove(a)}
                      >
                        {a.state === 'DRAFT' ? 'Delete' : 'Archive'}
                      </button>
                    )}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {composing && <AnnouncementComposer existingId={composing.id} onClose={closeComposer} onSaved={saved} />}
    </div>
  );
}

function audienceLine(a: ListItem): string {
  const people = a.audience.allRoles ? 'Everyone' : `${a.audience.roleCodes.length} role${a.audience.roleCodes.length === 1 ? '' : 's'}`;
  const farms = a.audience.allFarms
    ? 'every farm'
    : a.audience.farmNames.length === 1
      ? a.audience.farmNames[0]!
      : `${a.audience.farmNames.length} farms`;
  return `${people} · ${farms}`;
}

function windowLine(a: ListItem): string {
  const from = a.startsAt ? `from ${shortDate(a.startsAt)}` : '';
  const to = a.endsAt ? `until ${shortDate(a.endsAt)}` : 'until taken down';
  return [from, to].filter(Boolean).join(' ');
}

function StatePill({ state }: { state: string }) {
  const tone =
    state === 'LIVE' ? 'pill--ok' : state === 'EXPIRED' ? '' : state === 'SCHEDULED' ? 'pill--warn' : '';
  return <span className={`pill ${tone} announcements__state`}>{STATE_LABELS[state] ?? state}</span>;
}
